import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Activity, ArrowRightLeft, Package, Truck, Warehouse as WarehouseIcon,
  Search, ChevronLeft, ChevronRight, Loader2,
  ClipboardCheck, RotateCcw, ShoppingCart, Filter, X, ExternalLink, Briefcase,
  ChevronDown, Unlock, Loader2 as Spinner
} from 'lucide-react';
import { format } from 'date-fns';
import { parseUTC } from '../lib/timezone';
import api from '../lib/api';
import { useLiveRefresh } from '../hooks/useLiveRefresh';
import { toast } from 'sonner';

const ACTION_CONFIG = {
  transfer:     { icon: ArrowRightLeft, label: 'Transfer',              color: 'text-blue-600 dark:text-blue-400',    bg: 'bg-blue-50 dark:bg-blue-950/40' },
  receive:      { icon: ShoppingCart,   label: 'Receive',               color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/40' },
  adjust:       { icon: Activity,       label: 'Adjust',                color: 'text-amber-600 dark:text-amber-400',  bg: 'bg-amber-50 dark:bg-amber-950/40' },
  assign:       { icon: ClipboardCheck, label: 'Reserved for Job',      color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-950/40' },
  consume:      { icon: Package,        label: 'Consume',               color: 'text-red-600 dark:text-red-400',      bg: 'bg-red-50 dark:bg-red-950/40' },
  return:       { icon: RotateCcw,      label: 'Return',                color: 'text-teal-600 dark:text-teal-400',    bg: 'bg-teal-50 dark:bg-teal-950/40' },
  release_hold: { icon: Unlock,         label: 'Reservation Released',  color: 'text-cyan-600 dark:text-cyan-400',    bg: 'bg-cyan-50 dark:bg-cyan-950/40' },
};

function ActionBadge({ type }) {
  const config = ACTION_CONFIG[type] || ACTION_CONFIG.adjust;
  const Icon = config.icon;
  return (
    <span data-testid={`action-badge-${type}`} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium ${config.color} ${config.bg}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

function LocationCell({ type, name, id }) {
  if (!type && !name) return <span className="text-muted-foreground">—</span>;
  const isVehicle = type === 'vehicle';
  const isWarehouse = type === 'warehouse';
  const isJob = type === 'job';
  const isExternal = type === 'external';
  const Icon = isVehicle ? Truck : isWarehouse ? WarehouseIcon : isJob ? Briefcase : Package;
  const displayName = name || (isExternal ? 'External' : type ? type.charAt(0).toUpperCase() + type.slice(1) : '—');
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <span className="truncate">{displayName}</span>
    </span>
  );
}

function QuantityCell({ quantity, actionType }) {
  // Show dash for zero quantities or null
  if (quantity === 0 || quantity === null || quantity === undefined) {
    return <span className="text-muted-foreground">—</span>;
  }
  const isNegative = quantity < 0;
  const isPositive = quantity > 0;
  return (
    <span className={`tabular-nums font-medium ${isNegative ? 'text-red-600 dark:text-red-400' : isPositive ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>
      {isPositive ? '+' : ''}{quantity}
    </span>
  );
}

function ReferenceCell({ type, id, jobName }) {
  if (!type || !id) return <span className="text-muted-foreground">—</span>;

  let label = '';
  let href = null;

  if (type === 'po') {
    label = `PO ${id}`;
    href = '/purchase-orders';
  } else if (type === 'job') {
    label = jobName || `Job #${id}`;
    href = `/jobs`;
  } else if (type === 'transfer_request') {
    label = 'Transfer';
  } else if (type === 'ai_action') {
    label = 'via StockBot';
  } else if (type === 'manual') {
    label = 'Manual';
  } else {
    label = type.charAt(0).toUpperCase() + type.slice(1);
  }

  if (href) {
    return (
      <Link
        to={href}
        className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-muted hover:bg-accent text-primary hover:underline transition-colors"
        data-testid={`ref-link-${type}-${id}`}
        onClick={(e) => e.stopPropagation()}
      >
        {label}
        <ExternalLink className="w-2.5 h-2.5" />
      </Link>
    );
  }

  if (type === 'ai_action') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200/60 font-medium">
        {label}
      </span>
    );
  }

  return (
    <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{label}</span>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="p-2 rounded-lg bg-muted">
          <Icon className="w-4 h-4 text-muted-foreground" />
        </div>
        <div>
          <p className="text-2xl font-semibold tracking-tight">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function InventoryActivityPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ warehouses: [], vehicles: [] });
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedRows, setExpandedRows] = useState({});
  const [childrenCache, setChildrenCache] = useState({});
  const [allocationCache, setAllocationCache] = useState({});
  const [releasing, setReleasing] = useState({});

  // Filters from URL params
  const page = parseInt(searchParams.get('page') || '1');
  const actionType = searchParams.get('action_type') || '';
  const search = searchParams.get('search') || '';
  const days = searchParams.get('days') || '30';
  const warehouseId = searchParams.get('warehouse_id') || '';
  const vehicleId = searchParams.get('vehicle_id') || '';
  const [searchInput, setSearchInput] = useState(search);

  const updateParam = useCallback((key, value) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'page') next.set('page', '1');
      return next;
    });
  }, [setSearchParams]);

  // Fetch filter options once
  useEffect(() => {
    api.get('/inventory/activity/filters').then(res => {
      setFilterOptions(res.data);
    }).catch(() => {});
  }, []);

  // Fetch activity data (Phase 6: refreshTick re-runs it silently after writes, on focus and every 60s)
  const [refreshTick, setRefreshTick] = useState(0);
  const silentRefresh = useRef(false);
  useLiveRefresh(() => { silentRefresh.current = true; setRefreshTick((t) => t + 1); });
  useEffect(() => {
    let cancelled = false;
    const silent = silentRefresh.current;
    silentRefresh.current = false;
    const fetchData = async () => {
      if (!silent) setLoading(true);
      try {
        const params = { page, page_size: 50, days: parseInt(days) };
        if (actionType) params.action_type = actionType;
        if (search) params.search = search;
        if (warehouseId) params.warehouse_id = warehouseId;
        if (vehicleId) params.vehicle_id = vehicleId;

        const [actRes, statsRes] = await Promise.all([
          api.get('/inventory/activity', { params }),
          api.get('/inventory/activity/stats', { params: { days: parseInt(days) } }),
        ]);
        if (cancelled) return;
        setActivities(actRes.data.activities || []);
        setTotal(actRes.data.total || 0);
        setTotalPages(actRes.data.total_pages || 1);
        setStats(statsRes.data);
      } catch (err) {
        if (!cancelled && !silent) toast.error('Failed to load activity data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [page, actionType, search, days, warehouseId, vehicleId, refreshTick]);

  const handleSearch = (e) => {
    e.preventDefault();
    updateParam('search', searchInput.trim());
  };

  const clearFilters = () => {
    setSearchParams({ page: '1', days: '30' });
    setSearchInput('');
  };

  const hasFilters = actionType || search || warehouseId || vehicleId;

  // Group children under parents — hide child release_hold rows from the main list
  const parentChildMap = {};
  activities.forEach(a => {
    if (a.parent_activity_id) {
      if (!parentChildMap[a.parent_activity_id]) parentChildMap[a.parent_activity_id] = [];
      parentChildMap[a.parent_activity_id].push(a);
    }
  });
  const visibleActivities = activities.filter(a => !a.parent_activity_id);

  const toggleRow = async (activityId) => {
    const isOpen = expandedRows[activityId];
    setExpandedRows(prev => ({ ...prev, [activityId]: !isOpen }));
    if (!isOpen && !childrenCache[activityId]) {
      try {
        const [childRes, allocRes] = await Promise.all([
          api.get(`/inventory/activity/${activityId}/children`),
          api.get(`/inventory/activity/${activityId}/allocation`),
        ]);
        setChildrenCache(prev => ({ ...prev, [activityId]: childRes.data.children || [] }));
        setAllocationCache(prev => ({ ...prev, [activityId]: allocRes.data.allocation }));
      } catch {
        // If endpoints fail, we still have inline children from parentChildMap
        setChildrenCache(prev => ({ ...prev, [activityId]: parentChildMap[activityId] || [] }));
      }
    }
  };

  const handleRelease = async (activityId) => {
    setReleasing(prev => ({ ...prev, [activityId]: true }));
    try {
      await api.post(`/inventory/activity/${activityId}/release`);
      toast.success('Reservation released — stock is available again');
      // Refresh data
      setExpandedRows(prev => ({ ...prev, [activityId]: false }));
      setChildrenCache({});
      setAllocationCache({});
      // Trigger refetch
      const params = { page, page_size: 50, days: parseInt(days) };
      if (actionType) params.action_type = actionType;
      if (search) params.search = search;
      if (warehouseId) params.warehouse_id = warehouseId;
      if (vehicleId) params.vehicle_id = vehicleId;
      const [actRes, statsRes] = await Promise.all([
        api.get('/inventory/activity', { params }),
        api.get('/inventory/activity/stats', { params: { days: parseInt(days) } }),
      ]);
      setActivities(actRes.data.activities || []);
      setTotal(actRes.data.total || 0);
      setTotalPages(actRes.data.total_pages || 1);
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to release hold');
    } finally {
      setReleasing(prev => ({ ...prev, [activityId]: false }));
    }
  };

  const isExpandable = (a) => a.action_type === 'assign';
  const isReleased = (a) => a.status === 'released';

  function StatusPill({ status, hasChildren }) {
    if (status === 'released') {
      return (
        <span className="inline-flex items-center gap-2">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300" data-testid="status-pill-released">
            <Unlock className="w-2.5 h-2.5" />
            Released
          </span>
          {hasChildren && (
            <span className="text-[10px] font-medium text-muted-foreground">1 update</span>
          )}
        </span>
      );
    }
    return null;
  }

  function ExpandedReservation({ activity }) {
    const children = childrenCache[activity.id] || parentChildMap[activity.id] || [];
    const allocation = allocationCache[activity.id];
    const isReleasedRow = isReleased(activity);
    const canRelease = !isReleasedRow && allocation && allocation.status === 'allocated';
    const isConsumed = allocation && (allocation.status === 'used' || allocation.status === 'partially_used');
    const isReleasingNow = releasing[activity.id];

    return (
      <div className="px-4 py-3 bg-purple-50/50 dark:bg-purple-950/20 border-t border-purple-200/50 dark:border-purple-800/30 space-y-3" data-testid={`expanded-${activity.id}`}>
        {/* Reservation details */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <span className="text-muted-foreground block mb-0.5">Job / Reference</span>
            <span className="font-medium">{activity.to_location_name || activity.reference_id || '—'}</span>
          </div>
          <div>
            <span className="text-muted-foreground block mb-0.5">Reserved By</span>
            <span className="font-medium">{activity.user_name || '—'}</span>
          </div>
          <div>
            <span className="text-muted-foreground block mb-0.5">Quantity</span>
            <span className="font-medium">{activity.quantity}</span>
          </div>
          <div>
            <span className="text-muted-foreground block mb-0.5">Date</span>
            <span className="font-medium">{activity.created_at ? format(parseUTC(activity.created_at), 'MMM d, yyyy h:mm a') : '—'}</span>
          </div>
        </div>
        {activity.notes && (
          <p className="text-xs text-muted-foreground italic">{activity.notes}</p>
        )}

        {/* Release action */}
        {!isReleasedRow && (
          <div className="flex items-center gap-3">
            {canRelease && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs font-bold border-cyan-300 text-cyan-700 hover:bg-cyan-50 dark:border-cyan-700 dark:text-cyan-300 dark:hover:bg-cyan-950/30"
                onClick={() => handleRelease(activity.id)}
                disabled={isReleasingNow}
                data-testid={`release-hold-btn-${activity.id}`}
              >
                {isReleasingNow ? <Spinner className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5 mr-1.5" />}
                Release Hold
              </Button>
            )}
            {isConsumed && (
              <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                Parts have been used on this job — hold cannot be released
              </span>
            )}
          </div>
        )}

        {/* Child release events */}
        {children.length > 0 && (
          <div className="border-l-2 border-cyan-300 dark:border-cyan-700 pl-3 space-y-2 mt-2">
            {children.map(child => (
              <div key={child.id} className="flex items-center gap-3 py-1.5" data-testid={`child-activity-${child.id}`}>
                <ActionBadge type={child.action_type} />
                <span className="text-xs text-muted-foreground">by {child.user_name || '—'}</span>
                <span className="text-xs text-muted-foreground ml-auto">
                  {child.created_at ? format(parseUTC(child.created_at), 'MMM d, h:mm a') : '—'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="inventory-activity-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Inventory Activity</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Centralized audit trail of all inventory movements
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={days} onValueChange={(v) => updateParam('days', v)}>
              <SelectTrigger className="w-[130px]" data-testid="days-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="activity-stats">
            <StatCard label="Total Actions" value={stats.total_actions} icon={Activity} />
            <StatCard label="Transfers" value={stats.breakdown?.transfer || 0} icon={ArrowRightLeft} />
            <StatCard label="Received" value={stats.breakdown?.receive || 0} icon={ShoppingCart} />
            <StatCard label="Adjustments" value={stats.breakdown?.adjust || 0} icon={Activity} />
            <StatCard label="Assigned" value={stats.breakdown?.assign || 0} icon={ClipboardCheck} />
            <StatCard label="Consumed" value={(stats.breakdown?.consume || 0) + (stats.breakdown?.return || 0)} icon={Package} />
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-3">
            <div className="space-y-3">
              {/* Row 1: Search + Action Type */}
              <div className="flex items-center gap-2">
                <form onSubmit={handleSearch} className="flex-1 flex gap-2 min-w-0">
                  <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      data-testid="activity-search"
                      placeholder="Search items, part #, users, notes..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className="pl-9 h-9"
                    />
                  </div>
                  <Button type="submit" variant="secondary" size="sm" className="h-9 hidden sm:inline-flex" data-testid="activity-search-submit">Search</Button>
                </form>
                <Select value={actionType || 'all'} onValueChange={(v) => updateParam('action_type', v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[130px] sm:w-[150px] h-9 shrink-0" data-testid="action-type-filter">
                    <Filter className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                    <SelectItem value="receive">Receive</SelectItem>
                    <SelectItem value="adjust">Adjust</SelectItem>
                    <SelectItem value="assign">Reserved for Job</SelectItem>
                    <SelectItem value="release_hold">Reservation Released</SelectItem>
                    <SelectItem value="consume">Consume</SelectItem>
                    <SelectItem value="return">Return</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Row 2: Location Filters — compact flex-wrap */}
              <div className="flex gap-2 flex-wrap items-center">
                {filterOptions.warehouses.length > 0 && (
                  <Select value={warehouseId || 'all'} onValueChange={(v) => updateParam('warehouse_id', v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-[160px] h-9" data-testid="warehouse-filter">
                      <WarehouseIcon className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
                      <SelectValue placeholder="All Warehouses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Warehouses</SelectItem>
                      {filterOptions.warehouses.map(w => (
                        <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {filterOptions.vehicles.length > 0 && (
                  <Select value={vehicleId || 'all'} onValueChange={(v) => updateParam('vehicle_id', v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-[160px] h-9" data-testid="vehicle-filter">
                      <Truck className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
                      <SelectValue placeholder="All Vehicles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Vehicles</SelectItem>
                      {filterOptions.vehicles.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {hasFilters && (
                  <Button variant="ghost" size="sm" className="h-9" onClick={clearFilters} data-testid="clear-filters">
                    <X className="w-3.5 h-3.5 mr-1" /> Clear
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Activity Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center py-16">
                <Activity className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">No activity found</p>
                {hasFilters && (
                  <Button variant="link" size="sm" onClick={clearFilters} className="mt-2">Clear filters</Button>
                )}
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm" data-testid="activity-table">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left font-medium text-muted-foreground px-4 py-2.5 w-[100px]">Action</th>
                        <th className="text-left font-medium text-muted-foreground px-4 py-2.5">Item</th>
                        <th className="text-right font-medium text-muted-foreground px-4 py-2.5 w-[70px]">Qty</th>
                        <th className="text-left font-medium text-muted-foreground px-4 py-2.5">From</th>
                        <th className="text-left font-medium text-muted-foreground px-4 py-2.5">To</th>
                        <th className="text-left font-medium text-muted-foreground px-4 py-2.5">User</th>
                        <th className="text-left font-medium text-muted-foreground px-4 py-2.5">Reference</th>
                        <th className="text-right font-medium text-muted-foreground px-4 py-2.5 w-[140px]">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {visibleActivities.map((a) => (
                        <React.Fragment key={a.id}>
                          <tr
                            className={`transition-colors ${isExpandable(a) ? 'cursor-pointer hover:bg-purple-50/50 dark:hover:bg-purple-950/20' : 'hover:bg-muted/20'} ${expandedRows[a.id] ? 'bg-purple-50/30 dark:bg-purple-950/10' : ''}`}
                            onClick={isExpandable(a) ? () => toggleRow(a.id) : undefined}
                            data-testid={`activity-row-${a.id}`}
                          >
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-1.5">
                                {isExpandable(a) && (
                                  <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${expandedRows[a.id] ? 'rotate-180' : ''}`} />
                                )}
                                <ActionBadge type={a.action_type} />
                                <StatusPill status={a.status} hasChildren={isReleased(a)} />
                              </div>
                            </td>
                            <td className="px-4 py-2.5 max-w-[220px]">
                              <p className="font-medium truncate">{a.item_name || '—'}</p>
                              {a.item_manufacturer_part_number && <p className="text-[11px] text-muted-foreground font-mono truncate" data-testid={`activity-mpn-${a.id}`}>MPN {a.item_manufacturer_part_number}</p>}
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              <QuantityCell quantity={a.quantity} actionType={a.action_type} />
                            </td>
                            <td className="px-4 py-2.5"><LocationCell type={a.from_location_type} name={a.from_location_name} id={a.from_location_id} /></td>
                            <td className="px-4 py-2.5"><LocationCell type={a.to_location_type} name={a.to_location_name} id={a.to_location_id} /></td>
                            <td className="px-4 py-2.5 text-muted-foreground truncate max-w-[120px]">{a.user_name || '—'}</td>
                            <td className="px-4 py-2.5">
                              <ReferenceCell
                                type={a.reference_type}
                                id={a.reference_id}
                                jobName={a.reference_type === 'job' ? (a.to_location_name || a.from_location_name) : undefined}
                              />
                            </td>
                            <td className="px-4 py-2.5 text-right text-muted-foreground whitespace-nowrap text-xs">
                              {a.created_at ? format(parseUTC(a.created_at), 'MMM d, h:mm a') : '—'}
                            </td>
                          </tr>
                          {isExpandable(a) && expandedRows[a.id] && (
                            <tr>
                              <td colSpan={8} className="p-0">
                                <ExpandedReservation activity={a} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden divide-y">
                  {visibleActivities.map((a) => (
                    <div key={a.id}>
                      <div
                        className={`p-3 space-y-1.5 ${isExpandable(a) ? 'cursor-pointer active:bg-purple-50/50 dark:active:bg-purple-950/20' : ''} ${expandedRows[a.id] ? 'bg-purple-50/30 dark:bg-purple-950/10' : ''}`}
                        onClick={isExpandable(a) ? () => toggleRow(a.id) : undefined}
                        data-testid={`activity-card-${a.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            {isExpandable(a) && (
                              <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${expandedRows[a.id] ? 'rotate-180' : ''}`} />
                            )}
                            <ActionBadge type={a.action_type} />
                            <StatusPill status={a.status} />
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {a.created_at ? format(parseUTC(a.created_at), 'MMM d, h:mm a') : '—'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm truncate">{a.item_name || '—'}{a.item_manufacturer_part_number && <span className="ml-1.5 text-[11px] font-normal text-muted-foreground font-mono" data-testid={`activity-mpn-m-${a.id}`}>MPN {a.item_manufacturer_part_number}</span>}</span>
                          <QuantityCell quantity={a.quantity} actionType={a.action_type} />
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <LocationCell type={a.from_location_type} name={a.from_location_name} />
                          {(a.from_location_name || a.from_location_type) && (a.to_location_name || a.to_location_type) && <span>→</span>}
                          <LocationCell type={a.to_location_type} name={a.to_location_name} />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">{a.user_name || '—'}</span>
                          <ReferenceCell
                            type={a.reference_type}
                            id={a.reference_id}
                            jobName={a.reference_type === 'job' ? (a.to_location_name || a.from_location_name) : undefined}
                          />
                        </div>
                      </div>
                      {isExpandable(a) && expandedRows[a.id] && (
                        <ExpandedReservation activity={a} />
                      )}
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t" data-testid="activity-pagination">
                  <p className="text-xs text-muted-foreground">
                    Showing {((page - 1) * 50) + 1}–{Math.min(page * 50, total)} of {total}
                  </p>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline" size="icon" className="h-8 w-8"
                      disabled={page <= 1}
                      onClick={() => updateParam('page', String(page - 1))}
                      data-testid="prev-page"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-xs text-muted-foreground px-2">{page} / {totalPages}</span>
                    <Button
                      variant="outline" size="icon" className="h-8 w-8"
                      disabled={page >= totalPages}
                      onClick={() => updateParam('page', String(page + 1))}
                      data-testid="next-page"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
